/**
 * Light / dark / system theme.
 *
 * The choice is stored in localStorage as `theme` and applied as
 * `data-theme="dark"` on <html> (absence means light). index.html applies the
 * same rule inline before the first paint; this hook keeps it in step with
 * the system preference while the app is open.
 */
import { useCallback, useEffect, useSyncExternalStore } from 'react'

export type ThemeChoice = 'light' | 'dark' | 'system'

const KEY = 'theme'
const listeners = new Set<() => void>()

function readChoice(): ThemeChoice {
  try {
    const saved = localStorage.getItem(KEY)
    return saved === 'dark' || saved === 'light' ? saved : 'system'
  } catch {
    return 'system'
  }
}

function systemDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches
}

export function resolveTheme(choice: ThemeChoice = readChoice()): 'light' | 'dark' {
  return choice === 'system' ? (systemDark() ? 'dark' : 'light') : choice
}

function apply(choice: ThemeChoice) {
  const resolved = resolveTheme(choice)
  const root = document.documentElement
  if (resolved === 'dark') root.setAttribute('data-theme', 'dark')
  else root.removeAttribute('data-theme')
  for (const listener of listeners) listener()
}

export function setTheme(choice: ThemeChoice) {
  try {
    if (choice === 'system') localStorage.removeItem(KEY)
    else localStorage.setItem(KEY, choice)
  } catch {
    /* private mode: the choice lasts for this page only */
  }
  apply(choice)
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** `[choice, resolved, setTheme]` — `resolved` is what is on screen right now. */
export function useTheme(): [ThemeChoice, 'light' | 'dark', (choice: ThemeChoice) => void] {
  const choice = useSyncExternalStore(subscribe, readChoice, () => 'system' as ThemeChoice)
  const resolved = useSyncExternalStore(subscribe, () => resolveTheme(readChoice()), () => 'light' as const)

  // Follow the system while the choice is "system".
  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => {
      if (readChoice() === 'system') apply('system')
    }
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [])

  const set = useCallback((next: ThemeChoice) => setTheme(next), [])
  return [choice, resolved, set]
}
